import React from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import {
    getItemMap,
    getItemStates,
    getPreviewConfig,
    getShowPrice,
    getSummaryItems,
} from "../../store/sagas/selectors";
import { Item } from "../../models/item";
import { ADD_ITEM, REMOVE_ITEM } from "../../store/actions/configurationActions";
import { ConsequenceList } from "../ConsequenceList/ConsequenceList";
import { usePriceFormatter } from "../../utilities/priceFormatter";
import { useText } from "../../utilities/textUtil";
import GaReporter from "../../utilities/gaReporter";

const ReadmoreContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-content: stretch;
    align-items: flex-start;
    justify-content: space-between;
    font-size: 14px;
    color: #333333;
    ${({ theme }) => theme.bp.tb`font-size: 12px;`}
`;

const TextContainer = styled.div<TextProps>`
    width: 100%;
    padding: ${props => props.padding};
    box-sizing: border-box;
`;

const ItemTitle = styled.div`
    margin-bottom: 5px;
    font-weight: 500;
    font-size: 16px;
`;

/*
const ReadmoreText = styled.div`
    white-space: pre-wrap;
`;
*/

const AddButton = styled.div<ButtonProps>`
    height: ${props => props.height};
    background: rgba(255, 255, 255, 1);
    width: calc(100% - 50px);
    max-width: 208px;
    margin: ${props => props.margin};
    border: 1px solid #000;
    border-radius: 4px;
    color: #000;
    font-size: 14px;
    display: flex;
    align-items: center;
    align-self: center;
    justify-content: flex-start;
    cursor: pointer;
    transition: background 0.2s ease-in-out;
    padding: 0 16px;
    ${({ theme }) => theme.bp.landscape`
        padding: 0 14px 0 10px;
        height: 40px;
    `}
    ${({ theme }) => theme.bp.md`
        padding: 0 16px;
        height: 48px;
        width: calc(100% - 80px);
    `}
    box-sizing: border-box;
    &:hover {
        background: var(--light-hover-color);
    }

    &:active {
        background: var(--light-active-color);
    }

    & .material-icons {
        flex-basis: 35px;
    }
`;

const AddText = styled.span`
    flex-grow: 1;
    font-weight: 500;
`;

const SelectedText = styled.span`
    font-size: 14px;
    font-weight: 300;
    align-self: center;
    margin-top: 10px;
`;

const RemoveButton = styled.div<ButtonProps>`
    height: ${props => props.height}; 36px;
    width: calc(100% - 80px);
    max-width: 208px;
    margin: ${props => props.margin}; 2px 40px 15px 40px;
    border-radius: 4px;
    color: var(--primary-color);
    text-align: center;
    align-self: center;
    cursor: pointer;
    font-size: 14px;
    font-weight: 400;
    transition: background 0.2s ease-in-out;
    padding: 9px 16px 0;
    box-sizing: border-box;
    &:hover {
        background: var(--light-hover-color);
    }

    &:active {
        background: var(--light-active-color);
    }
`;

interface ButtonProps {
    height: string;
    margin: string;
}

interface TextProps {
    padding: string;
}

export const Readmore = ({ item: previewItem }: { item: Item }) => {
    const dispatch = useDispatch();
    const showPrice = useSelector(getShowPrice);
    const itemStates = useSelector(getItemStates);
    const summaryItems = useSelector(getSummaryItems);
    const previewConfig = useSelector(getPreviewConfig);
    const itemMap = useSelector(getItemMap);
    const t = useText();
    const priceFormatter = usePriceFormatter(false);

    const price = itemStates.getPrice(previewItem.id);
    const priceText = priceFormatter.formatDisplayPrice(price);

    const addedItems: Item[] = [];
    const removedItems: Item[] = [];
    if (previewConfig) {
        previewConfig.addedItems.forEach(itemId => {
            const item = itemMap[itemId];
            if (item && itemId !== previewItem.id) {
                addedItems.push(item);
            }
        });
        previewConfig.removedItems.forEach(itemId => {
            const item = itemMap[itemId];
            if (item && item.isPermanent) {
                removedItems.push(item);
            }
        });
    }

    const handleAdd = () => {
        dispatch({ type: ADD_ITEM, item: previewItem, requiredItems: addedItems, removedItems });
        GaReporter.trackItemActivation(previewItem, price);
        addedItems.forEach(item => {
            if (item.displayText || item.retailerId) {
                GaReporter.trackItemActivation(item, itemStates.getPrice(item.id));
            }
        });
    };

    const handleRemove = () => {
        dispatch({ type: REMOVE_ITEM, item: previewItem });
    };

    let addHeight = "48px";
    let addMargin = "15px 40px 25px 40px";
    let removeHeight = " 36px";
    let removeMargin = "2px 40px 15px 40px";
    let textPadding = "25px 25px 0";

    if (window.innerHeight < 610) {
        addHeight = "30px !important";
        addMargin = "10px 40px 15px 40px !important";
        removeHeight = "30px !important";
        removeMargin = "2px 40px 10px 40px !important";
        textPadding = "15px 25px 0";
    }

    return (
        <ReadmoreContainer>
            <TextContainer padding={textPadding}>
                <ItemTitle>{previewItem.displayText || previewItem.retailerId || previewItem.id}</ItemTitle>
                {/* <ReadmoreText>{item.description}</ReadmoreText> */}
            </TextContainer>
            {!previewItem.isPermanent || !summaryItems.some(s => s.item.id === previewItem.id) ? (
                <AddButton height={addHeight} margin={addMargin} onClick={handleAdd}>
                    <span className="material-icons">add_circle</span>
                    <AddText>{t("addProduct")}</AddText>
                    {showPrice && priceText ? <span>{priceText}</span> : null}
                </AddButton>
            ) : (
                <>
                    <SelectedText>{t("productSelected")}</SelectedText>
                    <RemoveButton height={removeHeight} margin={removeMargin} onClick={handleRemove}>
                        {t("removeProduct")}
                    </RemoveButton>
                </>
            )}
            {addedItems.length > 0 || removedItems.length > 0 ? (
                <ConsequenceList addedItems={addedItems} removedItems={removedItems} />
            ) : null}
        </ReadmoreContainer>
    );
};
